import { Component, ComponentInterface, Element, h, Host, Prop, State, Watch } from "@stencil/core";
import api from "../../utils/api";
import { buildQueryString } from "../../utils/buildQueryString";
import { Label } from "../label/label";
import { DirectSearchResponse, DirectSearchResult } from "./direct-search-response";

/** static base className */
const baseClass = "ux-direct-searchbox";

/** uniqueId increment value */
let uidIncrement = 0;

/** create a unique component id */
const getUniqueId = () => `direct-searchbox-${(++uidIncrement).toString().padStart(3, "0")}`;

@Component({
    tag: "ux-direct-searchbox",
    styleUrl: "direct-searchbox.scss",
    shadow: false
})
export class DirectSearchbox implements ComponentInterface {
    /** the searchbox dom element */
    @Element() domNode!: HTMLUxDirectSearchboxElement;

    /** an optional unique id for the input element */
    @Prop() readonly inputId?: string = "";
    /** the input name */
    @Prop() readonly name?: string = "q";
    /** the input label text */
    @Prop() readonly label?: string = "";
    /** additional info label for the the input field */
    @Prop() readonly labelInfo: string = "";
    /** the input placeholder text */
    @Prop() readonly placeholder?: string = "";
    /** the submit button label */
    @Prop() readonly buttonText?: string = "";
    /** the API endpoint from which data is requested */
    @Prop() readonly url?: string = "";
    /** flag that controls if the searchbox is disabled */
    @Prop({ reflect: true }) readonly disabled: boolean = false;
    /** flag that indicates that the search api has responded with an error */
    @Prop({ reflect: true, mutable: true }) error = false;
    /** flag that indicates a pending search request */
    @Prop({ reflect: true, mutable: true }) pending = false;
    /** an error message which is rendered in error state */
    @Prop({ reflect: true, mutable: true }) errorMsg: string | null = null;

    /** unique id used for form elements */
    @State() uniqueId = "";
    /** storage for the last search result */
    @State() searchResult: DirectSearchResult | null = null;

    @Watch("inputId")
    componentWillLoad() {
        this.uniqueId = this.inputId || getUniqueId();
    }

    @Watch("searchResult")
    handleSearchResult() {
        if (this.searchResult === null) {
            return;
        }
        const { redirectUrl } = this.searchResult;
        if (redirectUrl !== undefined) {
            // @ts-expect-error Check for typing
            window.top.location.href = redirectUrl;
            return;
        }
        if (this.searchInput !== null) {
            this.searchInput.value = "";
        }
    }

    /** search input element */
    private searchInput: HTMLInputElement | null = null;

    componentDidLoad() {
        this.searchInput = this.domNode.querySelector("input[type=text]") as HTMLInputElement;
    }

    /** Send the request and handle response */
    private requestData(url: string) {
        if (url === "" || this.searchInput === null || this.searchInput.value === "") {
            return;
        }
        const options = isMockUrl(url)
            ? {}
            : {
                  method: "POST",
                  body: buildQueryString({
                      // @ts-expect-error Check for typing
                      [this.name]: this.searchInput.value
                  }),
                  headers: {
                      "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8"
                  }
              };
        this.errorMsg = null;
        this.error = false;
        this.pending = true;
        api<DirectSearchResponse>(url, options)
            .then((data) => {
                if (data.result !== null) {
                    this.searchResult = data.result;
                } else if (data.error !== null) {
                    // @ts-expect-error Check for typing
                    this.errorMsg = data.error.message;
                    this.error = true;
                }
                this.pending = false;
            })
            .catch((error) => {
                this.errorMsg = error.message;
                this.error = true;
                this.pending = false;
            });
    }

    private handleFormEvent(e: Event) {
        switch (e.type) {
            case "change":
                this.error = false;
                break;
            case "submit":
                e.preventDefault();
                // @ts-expect-error Check for typing
                this.requestData(this.url);
                break;
            default:
                break;
        }
    }

    render() {
        const { label, labelInfo, error, errorMsg, disabled, pending, name, uniqueId } = this;
        return (
            <Host
                class={{
                    [baseClass]: true,
                    [`${baseClass}--disabled`]: disabled,
                    [`${baseClass}--error`]: error,
                    [`${baseClass}--pending`]: pending
                }}
            >
                <form
                    autoComplete="off"
                    autoCapitalize="off"
                    class={`${baseClass}-form`}
                    aria-disabled={disabled}
                    aria-invalid={error ? "true" : "false"}
                    onChange={(e) => this.handleFormEvent(e)}
                    onSubmit={(e) => this.handleFormEvent(e)}
                >
                    {/* @ts-expect-error Check for typing */}
                    <Label label={label} labelInfo={labelInfo} error={error} errorMsg={errorMsg} htmlFor={uniqueId} />
                    <div class={`${baseClass}-controls`}>
                        <input
                            class={{
                                "ux-input": true,
                                "is-error": error
                            }}
                            disabled={disabled}
                            id={uniqueId}
                            name={name}
                            placeholder={this.placeholder}
                            type="text"
                            aria-invalid={error ? "true" : "false"}
                            onChange={(e) => this.handleFormEvent(e)}
                        />
                        <span class={`${baseClass}-controls__spacer`} aria-hidden />
                        <button class="ux-button ux-button--green" disabled={disabled} type="submit">
                            {this.buttonText}
                        </button>
                        {pending && <span class="ux-spinner ux-spinner--absolute" />}
                    </div>
                </form>
            </Host>
        );
    }
}

function isMockUrl(url: string) {
    return url.includes("/mock-data/");
}
